'use client';

import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { Check, LucideIcon } from 'lucide-react';

export interface FeatureItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface LandingFeaturesProps {
  title: string;
  features: FeatureItem[];
  gradientFrom: string;
  gradientTo: string;
}

export function LandingFeatures({
  title,
  features,
  gradientFrom,
  gradientTo,
}: LandingFeaturesProps) {
  return (
    <section className="py-24 bg-neutral-50/50 dark:bg-neutral-900/50 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#ffffff10_1px,transparent_1px)] bg-size-[20px_20px] opacity-30" />

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
            {title}
          </h2>
          <div
            className="h-1.5 w-24 mx-auto rounded-full"
            style={{
              background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
            }}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative p-8 rounded-3xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:border-transparent dark:hover:border-transparent transition-all duration-300 hover:shadow-2xl hover:shadow-neutral-200/50 dark:hover:shadow-none overflow-hidden"
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: `linear-gradient(135deg, ${gradientFrom}10, ${gradientTo}10)`,
                }}
              />

              <div className="relative z-10 flex items-start gap-6">
                <div
                  className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center shadow-xs group-hover:scale-110 transition-transform duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${gradientFrom}15, ${gradientTo}10)`,
                  }}
                >
                  <feature.icon
                    className="w-7 h-7"
                    style={{ color: gradientFrom }}
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed text-base">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
